/**
 * The colours of the painter's within-field variety (docs/STYLE.md §12.18, fieldTexture.ts): the
 * tone directions as multipliers of the field colour, and the streaks' colour.
 */
import { linearToLab, type Lab, type LinearRGB } from '../core/color';
import type { PaletteModel } from '../data/types';

/**
 * The painting's within-field colour variety the render lacked, as Lab directions and standard
 * deviations of the field colour. In the painter view's far fields, within their parcels, the
 * painting's covariance less the render's without the tone has two principal directions: darker and
 * yellower against lighter and bluer (2.69), and redder against greener (1.73), which holds the a*
 * scatter the render lacked (1.42 against the painting's 2.20). The painter view keeps about 0.7 of
 * a direction's standard deviation through the Kuwahara pass and the haze, so the tone carries 3.8
 * and 2.5, the whole of both. Until the colour map took the parcel tone's place where it stands whole
 * (fieldColour.ts), the two stacked: carried whole, the darker direction took the painted polders'
 * strip contrast past the painting's and the redder one the mid band's fields into polderFields, and
 * the tone held 1.7 and 1.8. Without the stacking the whole of both brings the field's fine spread and
 * the strip contrast to the painting's (terrain-colour-2, 2026-09-28).
 */
export const FIELD_TONE_AXES = [
  { lab: [-0.68, 0.08, 0.73] as Lab, std: 3.8 },
  { lab: [0.15, 0.99, 0.03] as Lab, std: 2.5 },
] as const;

function scaled(c: LinearRGB, logMultipliers: readonly number[]): LinearRGB {
  return [
    c[0] * Math.exp(logMultipliers[0]!),
    c[1] * Math.exp(logMultipliers[1]!),
    c[2] * Math.exp(logMultipliers[2]!),
  ];
}

function solve3(m: readonly (readonly number[])[], v: readonly number[]): LinearRGB {
  const det = (a: readonly (readonly number[])[]): number =>
    a[0]![0]! * (a[1]![1]! * a[2]![2]! - a[1]![2]! * a[2]![1]!) -
    a[0]![1]! * (a[1]![0]! * a[2]![2]! - a[1]![2]! * a[2]![0]!) +
    a[0]![2]! * (a[1]![0]! * a[2]![1]! - a[1]![1]! * a[2]![0]!);
  const d = det(m);
  const column = (k: number): number =>
    det(m.map((row, i) => row.map((value, j) => (j === k ? v[i]! : value)))) / d;
  return [column(0), column(1), column(2)];
}

function labOffset(base: LinearRGB, logMultipliers: readonly number[]): Lab {
  const moved = linearToLab(scaled(base, logMultipliers));
  const start = linearToLab(base);
  return [moved[0] - start[0], moved[1] - start[1], moved[2] - start[2]];
}

/* Jacobian of the Lab offset in the log channels at `logMultipliers`, by central differences, as
   rows of Lab components. */
function labJacobian(base: LinearRGB, logMultipliers: readonly number[]): number[][] {
  const step = 1e-4;
  const columns = [0, 1, 2].map((channel) => {
    const nudged = (sign: number): number[] =>
      logMultipliers.map((m, k) => (k === channel ? m + sign * step : m));
    const up = labOffset(base, nudged(1));
    const down = labOffset(base, nudged(-1));
    return [0, 1, 2].map((k) => (up[k]! - down[k]!) / (2 * step));
  });
  return [0, 1, 2].map((k) => columns.map((column) => column[k]!));
}

/**
 * Log multipliers of the linear channels that move `base` by `std` along the Lab `direction`, by
 * Newton steps on the Lab offset from no change.
 */
export function toneLogMultipliers(base: LinearRGB, direction: Lab, std: number): LinearRGB {
  const length = Math.hypot(direction[0], direction[1], direction[2]);
  const target = direction.map((c) => (c / length) * std);
  let multipliers: LinearRGB = [0, 0, 0];
  for (let iteration = 0; iteration < 4; iteration++) {
    const offset = labOffset(base, multipliers);
    const step = solve3(
      labJacobian(base, multipliers),
      target.map((t, k) => t - offset[k]!)
    );
    multipliers = [multipliers[0] + step[0], multipliers[1] + step[1], multipliers[2] + step[2]];
  }
  return multipliers;
}

export interface FieldTextureColours {
  /** Log multipliers of one standard deviation of each tone direction. */
  readonly toneA: LinearRGB;
  readonly toneB: LinearRGB;
  /** Multiplier of the field colour in a streak's core: the painting's trees over its far field. */
  readonly streak: LinearRGB;
}

export function fieldTextureColours(palette: PaletteModel): FieldTextureColours {
  const field = palette.farFields.median.linear;
  const tree = palette.treeFoliage.median.linear;
  const [a, b] = FIELD_TONE_AXES;
  return {
    toneA: toneLogMultipliers(field, a.lab, a.std),
    toneB: toneLogMultipliers(field, b.lab, b.std),
    streak: [tree[0] / field[0], tree[1] / field[1], tree[2] / field[2]],
  };
}
